// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { Call, LocalAudioStream } from '@azure/communication-calling';
import { CallWithChatAdapter } from '@azure/communication-react';
import { DefaultButton, Modal, PrimaryButton, Stack } from '@fluentui/react';
import React, { useEffect, useRef, useState } from 'react';
import { createBeepAudioTrackToSend, loadSoundEffectAudio } from 'utils/AudioHelpers';
import { utils } from 'utils/Utils';
import { SoundEffectCard } from './SoundEffectCard';
import { SoundEffectsModalHeader } from './SoundEffectsHeader';
import { useBranding } from 'theme/SwitchableThemeProvider';

export interface SoundEffectsModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  adapter: CallWithChatAdapter;
}

export const SoundEffectsModal = (props: SoundEffectsModalProps): JSX.Element => {
  const soundEffects = useBranding().soundEffects;
  const call = useHackyCall(props.adapter);
  const muteAfterSoundEffect = useRef(false);
  const [soundPlaying, setSoundPlaying] = useState<false | 'Preview' | 'CallSound'>(false);
  const currentSoundDuration = useRef<number>(0);
  const cancelSoundEffect = useRef(false);
  const currentPreviewAudio = useRef<HTMLMediaElement>();
  const currentCallAudio = useRef<HTMLMediaElement>();
  const [selectedSoundEffect, setSelectedSoundEffect] = useState<string>(soundEffects[0].soundName);

  const startSoundEffect = async (soundEffectName: string, previewOnly: boolean): Promise<number> => {
    cancelSoundEffect.current = false;

    // Play locally so the user can hear their sound effect
    currentPreviewAudio.current = await loadSoundEffectAudio(soundEffectName, currentPreviewAudio.current);
    currentPreviewAudio.current.play();

    // Play sound effect in the call
    if (!previewOnly) {
      muteAfterSoundEffect.current = call?.isMuted || muteAfterSoundEffect.current;
      currentCallAudio.current = await loadSoundEffectAudio(soundEffectName);
      const soundEffect = await createBeepAudioTrackToSend(currentCallAudio.current);
      const localAudioStream = new LocalAudioStream(soundEffect);
      await call?.unmute();
      currentCallAudio.current.play();
      await call?.startAudio(localAudioStream);
    }

    return currentPreviewAudio.current.duration * 1000;
  };

  const stopSoundEffect = (): void => {
    cancelSoundEffect.current = true;
    currentPreviewAudio.current?.pause();
    currentCallAudio.current?.pause();
    try {
      call?.stopAudio();
      if (muteAfterSoundEffect.current) {
        muteAfterSoundEffect.current = false;
        call?.mute();
      }
    } finally {
      setSoundPlaying(false);
      currentSoundDuration.current = 0;
    }
  };

  const playSoundEffect = async (soundEffectName: string, previewOnly: boolean): Promise<void> => {
    setSoundPlaying(previewOnly ? 'Preview' : 'CallSound');
    try {
      const duration = await startSoundEffect(soundEffectName, previewOnly);
      const bufferMs = 400; // play sound effect for longer than the audio to ensure it plays to the end
      while (currentSoundDuration.current < duration + bufferMs && !cancelSoundEffect.current) {
        currentSoundDuration.current += 100;
        await utils.delay(50);
      }
    } finally {
      // check if we already called stop sound effects
      if (!cancelSoundEffect.current) {
        stopSoundEffect();
      }
    }
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onDismiss={props.onDismiss}
      styles={{ main: { padding: '1.5rem', borderRadius: '0.5rem' } }}
    >
      <Stack tokens={{ childrenGap: '1.5rem' }}>
        <SoundEffectsModalHeader onCloseClick={props.onDismiss} />
        <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: '1.5rem' }}>
          {soundEffects.map((soundEffect) => (
            <SoundEffectCard
              key={soundEffect.soundName}
              title={soundEffect.displayText}
              icon={soundEffect.icon}
              disabled={!!soundPlaying && selectedSoundEffect !== soundEffect.soundName}
              active={selectedSoundEffect === soundEffect.soundName}
              duration={soundEffect.durationMs}
              currentDuration={selectedSoundEffect === soundEffect.soundName ? currentSoundDuration.current : 0}
              isSelected={selectedSoundEffect === soundEffect.soundName}
              onSelect={() => !soundPlaying && setSelectedSoundEffect(soundEffect.soundName)}
            />
          ))}
        </Stack>
        <Stack horizontal tokens={{ childrenGap: '1rem' }}>
          <DefaultButton
            disabled={soundPlaying === 'CallSound'}
            onClick={() => (soundPlaying ? stopSoundEffect() : playSoundEffect(selectedSoundEffect, true))}
          >
            {soundPlaying === 'Preview' ? 'Stop Preview' : 'Preview Effect'}
          </DefaultButton>
          <PrimaryButton
            disabled={soundPlaying === 'Preview'}
            onClick={() => (soundPlaying ? stopSoundEffect() : playSoundEffect(selectedSoundEffect, false))}
          >
            {soundPlaying === 'CallSound' ? 'Stop Effect' : 'Play Effect'}
          </PrimaryButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

/**
 * Currently the adapter does not support triggering audio in a call.
 * This is a hack to get the call object from the adapter.
 */
const useHackyCall = (adapter: CallWithChatAdapter): Call | undefined => {
  const [call, setCall] = useState<Call>();

  useEffect(() => {
    const onStateChange = (): void => {
      const hackilyGetCallObject = (adapter: CallWithChatAdapter): Call | undefined => {
        return adapter['callAdapter']._call;
      };

      setCall(hackilyGetCallObject(adapter));
    };
    adapter.onStateChange(onStateChange);
    return () => adapter.offStateChange(onStateChange);
  }, [adapter]);

  return call;
};
